'use client';

import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import {
  Phone,
  Mail,
  Clock,
  MapPin,
  Send,
  ArrowRight,
  ExternalLink,
  MessageCircle,
} from 'lucide-react';
import CompanyCrm from '../lib/CompanyCrm';

const DynamicMap = dynamic(() => import('../lib/DynamicMap'), { ssr: false });

const ContactUs = () => {
  const [isClient, setIsClient] = useState(false);
  const [activeSection, setActiveSection] = useState('contact');
  const [isMapScrollable, setIsMapScrollable] = useState(false);
  const mapContainerRef = useRef(null);
  const locationSectionRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const messageFormRef = useRef(null);

  useEffect(() => {
    setIsClient(true); // This will set to true only in the client-side

    // Show scroll hint for 5 seconds, then fade it out
    const timer = setTimeout(() => {
      setShowScrollHint(false);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  // Handle key down for map scrolling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey) {
        setIsMapScrollable(true);
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey) {
        setIsMapScrollable(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle tab switching without scrolling to top
  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  // Add a function to handle scrolling to the message form
  const scrollToMessageForm = () => {
    // First ensure we're on the contact tab
    setActiveSection('contact');

    // Use setTimeout to ensure the contact section is rendered before scrolling
    setTimeout(() => {
      if (messageFormRef.current) {
        // Scroll to the element with an offset to show the heading
        const yOffset = -120; // Adjust this value to control how much of the top part is visible
        const element = messageFormRef.current;
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 2,
      repeat: Number.POSITIVE_INFINITY,
      ease: 'easeInOut',
    },
  };

  return (
    <div className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>
      {/* Hero Section */}
      <section className='relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20'>
        {/* Background with animated gradient */}
        <div className='absolute inset-0 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 z-0'>
          <motion.div
            className='absolute inset-0 opacity-30 dark:opacity-40'
            animate={{
              background: [
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 80% 70%, rgba(56, 245, 239, 0.15) 0%, rgba(254, 42, 83, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
              ],
            }}
            transition={{
              duration: 15,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* Decorative elements */}
        <div className='absolute inset-0 overflow-hidden z-0'>
          {/* Top left circle */}
          <motion.div
            className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-primarycolor/5 dark:bg-primarycolor/10'
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 20,
              ease: 'easeInOut',
            }}
          />

          {/* Bottom right circle */}
          <motion.div
            className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-secondarycolor/5 dark:bg-secondarycolor/10'
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 25,
              ease: 'easeInOut',
            }}
          />

          {/* Small floating elements */}
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`floating-${i}`}
              className={`absolute w-${(i % 3) + 2} h-${(i % 3) + 2} rounded-full ${
                i % 3 === 0
                  ? 'bg-primarycolor/20'
                  : i % 3 === 1
                    ? 'bg-secondarycolor/20'
                    : 'bg-[#D4AF37]/20'
              }`}
              style={{
                top: `${10 + i * 8}%`,
                left: `${5 + i * 9}%`,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, 10, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 5 + i,
                ease: 'easeInOut',
                delay: i * 0.3,
              }}
            />
          ))}
        </div>

        <div className='container mx-auto px-4 z-10 py-20'>
          <div className='max-w-7xl mx-auto'>
            {/* Section title with animated underline */}
            <motion.div
              className='text-center mb-16'
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className='text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primarycolor to-secondarycolor bg-clip-text text-transparent'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Get in Touch
              </motion.h1>
              <motion.div
                className='h-1 w-24 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto'
                initial={{ width: 0 }}
                animate={{ width: 120 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
              <motion.p
                className='mt-6 text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                We're here to help you find your dream property in Dubai. Reach
                out to our team of experts today.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className='py-8 px-4 top-16 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md z-30 border-b border-gray-200 dark:border-gray-800'>
        <div className='container mx-auto'>
          <div className='flex justify-center'>
            <div className='inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1'>
              <button
                onClick={() => handleSectionChange('contact')}
                className={`px-6 py-2.5 rounded-full text-lg font-medium transition-all duration-300 ${
                  activeSection === 'contact'
                    ? 'bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor text-white dark:text-black shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <span className='flex items-center'>
                  <MessageCircle className='w-4 h-4 mr-2' />
                  Contact Us
                </span>
              </button>
              <button
                onClick={() => handleSectionChange('location')}
                className={`px-6 py-2.5 rounded-full text-lg font-medium transition-all duration-300 ${
                  activeSection === 'location'
                    ? 'bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor text-white dark:text-black shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <span className='flex items-center'>
                  <MapPin className='w-4 h-4 mr-2' />
                  Our Location
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information and Form Section */}
      <AnimatePresence mode='wait'>
        {activeSection === 'contact' && (
          <motion.section
            className='py-16 px-4'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className='container mx-auto'>
              <motion.div
                className='grid grid-cols-1 lg:grid-cols-2 gap-12'
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                {/* Contact Information Card */}
                <motion.div variants={fadeInLeft}>
                  <div className='relative p-8 rounded-3xl shadow-xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 backdrop-blur-sm border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
                    {/* Animated background pattern */}
                    <div className='absolute inset-0 opacity-5 dark:opacity-10'>
                      <motion.div
                        className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                        animate={{
                          backgroundPosition: ['0% 0%', '100% 100%'],
                        }}
                        transition={{
                          duration: 50,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: 'reverse',
                        }}
                      />
                    </div>

                    {/* Decorative corner accent */}
                    <div className='absolute top-0 left-0 w-20 h-20 overflow-hidden'>
                      <div className='absolute top-0 left-0 w-20 h-20 bg-primarycolor/10 dark:bg-primarycolor/20 transform -translate-x-1/2 -translate-y-1/2 rotate-45'></div>
                    </div>

                    {/* Gold corner accents */}
                    <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl z-10'></div>
                    <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] rounded-br-3xl z-10'></div>

                    <div className='relative z-10'>
                      <motion.h2
                        className='text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center'
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                      >
                        Visit Our Office
                      </motion.h2>

                      <div className='space-y-8'>
                        <motion.div
                          className='flex items-start'
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4 group-hover:bg-primarycolor/20'>
                            <motion.div animate={pulseAnimation}>
                              <MapPin className='w-6 h-6 text-primarycolor dark:text-secondarycolor' />
                            </motion.div>
                          </div>
                          <div>
                            <h4 className='text-lg font-semibold text-gray-900 dark:text-white mb-1'>
                              Address
                            </h4>
                            <p
                              className='text-gray-700 dark:text-gray-300'
                              itemProp='location'
                            >
                              Dubai-Showroom - 13 Ras Al Khor Rd - Nadd Al Hamar
                              - Dubai
                            </p>
                          </div>
                        </motion.div>

                        <motion.div
                          className='flex items-start'
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4'>
                            <motion.div animate={pulseAnimation}>
                              <Phone className='w-6 h-6 text-primarycolor dark:text-secondarycolor' />
                            </motion.div>
                          </div>
                          <div>
                            <h4 className='text-lg font-semibold text-gray-900 dark:text-white mb-1'>
                              Phone
                            </h4>
                            <a
                              href='tel:04-39 651 99'
                              className='text-gray-700 dark:text-gray-300 hover:text-primarycolor dark:hover:text-secondarycolor transition-colors'
                              itemProp='phone'
                            >
                              04-39 651 99
                            </a>
                          </div>
                        </motion.div>

                        <motion.div
                          className='flex items-start'
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4'>
                            <motion.div animate={pulseAnimation}>
                              <Mail className='w-6 h-6 text-primarycolor dark:text-secondarycolor' />
                            </motion.div>
                          </div>
                          <div>
                            <h4 className='text-lg font-semibold text-gray-900 dark:text-white mb-1'>
                              Email
                            </h4>
                            <a
                              href='mailto:info@x-space.ae'
                              className='text-gray-700 dark:text-gray-300 hover:text-primarycolor dark:hover:text-secondarycolor transition-colors'
                              itemProp='mail'
                            >
                              info@x-space.ae
                            </a>
                          </div>
                        </motion.div>

                        <motion.div
                          className='flex items-start'
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4'>
                            <motion.div animate={pulseAnimation}>
                              <Clock className='w-6 h-6 text-primarycolor dark:text-secondarycolor' />
                            </motion.div>
                          </div>
                          <div>
                            <h4 className='text-lg font-semibold text-gray-900 dark:text-white mb-1'>
                              Office Hours
                            </h4>
                            <p
                              className='text-gray-700 dark:text-gray-300'
                              itemProp='office-timing'
                            >
                              Mon - Sat: 9:30 AM - 6:30 PM
                            </p>
                          </div>
                        </motion.div>
                      </div>

                      {/* Social Media Links */}
                      <div className='mt-12'>
                        <h4 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center'>
                          Connect With Us
                        </h4>
                        <div className='flex justify-center space-x-4'>
                          <motion.a
                            href='https://www.facebook.com/xspacerealestate?mibextid=eQY6cl'
                            whileHover={{
                              y: -5,
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                            }}
                            className='w-12 h-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#0077B5] dark:hover:bg-[#0077B5] dark:hover:bg-blue-900/30 hover:text-white transition-colors'
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              fill='currentColor'
                              viewBox='0 0 16 16'
                            >
                              <path d='M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z' />
                            </svg>
                          </motion.a>
                          <motion.a
                            href='https://www.instagram.com/xspace_realestate?igsh=MTQ4djZoOXBwMDMxYQ=='
                            whileHover={{
                              y: -5,
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                            }}
                            className='w-12 h-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#E4405F] dark:hover:bg-[#E4405F] hover:text-white transition-colors'
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              fill='currentColor'
                              viewBox='0 0 16 16'
                            >
                              <path d='M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z' />
                            </svg>
                          </motion.a>
                          <motion.a
                            href='https://www.linkedin.com/feed/update/urn:li:activity:7176518870264938498'
                            whileHover={{
                              y: -5,
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                            }}
                            className='w-12 h-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#0077B5] dark:hover:bg-[#0077B5] dark:hover:bg-blue-900/30 hover:text-white transition-colors'
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='20'
                              height='20'
                              fill='currentColor'
                              viewBox='0 0 16 16'
                            >
                              <path d='M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z' />
                            </svg>
                          </motion.a>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Contact Form Card */}
                <motion.div variants={fadeInRight} ref={messageFormRef}>
                  <div className='relative p-8 rounded-3xl shadow-xl bg-white dark:bg-gray-900 backdrop-blur-sm border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
                    {/* Animated background pattern */}
                    <div className='absolute inset-0 opacity-5 dark:opacity-10'>
                      <motion.div
                        className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                        animate={{
                          backgroundPosition: ['100% 100%', '0% 0%'],
                        }}
                        transition={{
                          duration: 50,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: 'reverse',
                        }}
                      />
                    </div>

                    {/* Decorative corner accent */}
                    <div className='absolute top-0 right-0 w-20 h-20 overflow-hidden'>
                      <div className='absolute top-0 right-0 w-20 h-20 bg-secondarycolor/10 dark:bg-secondarycolor/20 transform translate-x-1/2 -translate-y-1/2 rotate-45'></div>
                    </div>

                    {/* Gold corner accents */}
                    <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-3xl z-10'></div>
                    <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-3xl z-10'></div>

                    <div className='relative z-10'>
                      <motion.h2
                        className='text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center'
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                      >
                        Send Us a Message
                      </motion.h2>

                      <div className='relative z-10'>
                        <CompanyCrm />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.section>
        )}

        {/* Map Section */}
        {activeSection === 'location' && (
          <motion.section
            ref={locationSectionRef}
            className='py-16 px-4'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className='container mx-auto'>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='text-center mb-16'
              >
                <h2 className='text-4xl font-bold text-gray-900 dark:text-white mb-4'>
                  Our Location
                </h2>
                <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
                <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
                  Visit our office in Dubai to discuss your real estate needs in
                  person.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='relative'
              >
                {/* Decorative elements */}
                <div className='absolute -top-6 -left-6 w-24 h-24 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 z-0'></div>
                <div className='absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-secondarycolor/10 dark:bg-secondarycolor/20 z-0'></div>

                {/* Gold corner accents */}
                <div className='absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37] z-10'></div>
                <div className='absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37] z-10'></div>
                <div className='absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37] z-10'></div>
                <div className='absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37] z-10'></div>

                <div
                  className='relative rounded-3xl overflow-hidden shadow-2xl'
                  ref={mapContainerRef}
                >
                  <div className='absolute inset-0 bg-gradient-to-br from-primarycolor/5 to-secondarycolor/5 z-10 pointer-events-none'></div>

                  {/* Map Scroll Instruction */}
                  <AnimatePresence>
                    {showScrollHint && (
                      <motion.div
                        className='absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-black/70 text-white px-4 py-2 rounded-full flex items-center shadow-lg'
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{
                            duration: 1.5,
                            repeat: Number.POSITIVE_INFINITY,
                          }}
                          className='mr-2'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                            />
                          </svg>
                        </motion.div>
                        Hold <span className='mx-1 font-bold'>Ctrl</span> to
                        scroll the map
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {isClient && (
                    <DynamicMap
                      latitude={25.185776930987412}
                      longitude={55.372077338460535}
                      propertyPic={
                        <div className='flex justify-center'>
                          <Image
                            src='https://images.x-space.ae/assets/images/logo-A.png'
                            alt='XSpace'
                            width={100}
                            height={50}
                          />
                        </div>
                      }
                      propertyRef={'XSpace Real Estate'}
                      height={'h-[30rem]'}
                      isScrollable={isMapScrollable}
                    />
                  )}

                  {/* Map Controls */}
                  <div className='absolute bottom-4 right-4 z-20 flex flex-col gap-2'>
                    <motion.a
                      href={
                        'https://www.google.com/maps/dir/?api=1&destination=25.1857187,55.3720988'
                      }
                      target='_blank'
                      rel='noopener noreferrer'
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className='bg-white dark:bg-gray-800 p-3 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                      title='Get Directions'
                    >
                      <ExternalLink className='w-5 h-5 text-primarycolor dark:text-secondarycolor' />
                    </motion.a>
                  </div>
                </div>

                {/* Address Card */}
                <motion.div
                  className='mt-8 bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-800'
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className='flex items-start'>
                    <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4'>
                      <MapPin className='w-6 h-6 text-primarycolor dark:text-secondarycolor' />
                    </div>
                    <div>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-1'>
                        Our Office Address
                      </h3>
                      <p className='text-gray-700 dark:text-gray-300'>
                        Dubai-Showroom - 13 Ras Al Khor Rd - Nadd Al Hamar -
                        Dubai
                      </p>
                      <p className='text-gray-600 dark:text-gray-400 mt-2 text-sm'>
                        Landmark: Near Dubai Festival City
                      </p>
                      <a
                        href={
                          'https://www.google.com/maps/dir/?api=1&destination=25.1857187,55.3720988'
                        }
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center mt-3 text-primarycolor dark:text-secondarycolor hover:underline'
                      >
                        Get Directions
                        <ArrowRight className='w-4 h-4 ml-1' />
                      </a>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Call to Action Section */}
      <section className='py-16 px-4 mb-16'>
        <div className='container mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='relative overflow-hidden rounded-3xl bg-gradient-to-r from-primarycolor/10 to-secondarycolor/10 dark:from-primarycolor/20 dark:to-secondarycolor/20 shadow-xl'
          >
            {/* Luxury pattern background with animation */}
            <div className='absolute inset-0 opacity-5 dark:opacity-10'>
              <motion.div
                className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                animate={{
                  backgroundPosition: ['0% 0%', '100% 100%'],
                }}
                transition={{
                  duration: 30,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: 'reverse',
                }}
              />
            </div>

            {/* Gold corner accents */}
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl z-10'></div>
            <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-3xl z-10'></div>
            <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-3xl z-10'></div>
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] rounded-br-3xl z-10'></div>

            <div className='p-12 relative z-10 text-center'>
              <motion.h2
                className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Looking for Your{' '}
                <span className='text-[#D4AF37]'>Dream Property</span> in Dubai?
              </motion.h2>
              <motion.p
                className='text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Our team of experts is ready to help you find the perfect
                property. Whether you're buying, selling, or investing, we're
                here to guide you every step of the way.
              </motion.p>
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToMessageForm}
                className='px-8 py-4 bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor border-2 border-primarycolor dark:border-secondarycolor rounded-full font-medium hover:bg-primarycolor hover:text-white dark:hover:bg-secondarycolor dark:hover:text-black transition-colors shadow-lg flex items-center mx-auto'
              >
                <Send className='w-5 h-5 mr-2' />
                Contact Us Today
                <ArrowRight className='w-5 h-5 ml-2' />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
